import { Link } from "react-router-dom";
import { ArrowRight, AlertTriangle } from "lucide-react";

const SECTIONS = [
  { n: "01", title: "Warehouse Operations", body: "Daily warehouse flow — intake, staging, inventory, outbound preparation and the standards that keep a running operation clean and safe.", tone: "orange", color: "#F7931E" },
  { n: "02", title: "Equipment Cleaning", body: "Approved cleaning agents, drying, storage, deep-cleans, patching and preventative maintenance procedures.", tone: "blue", color: "#168CFF" },
  { n: "03", title: "Rolling & Loading", body: "Efficient rolling techniques, dolly handling and trailer packing to maximize volume, speed and safety.", tone: "green", color: "#78C800" },
  { n: "04", title: "Delivery & Transportation", body: "Route planning, pre-trip checks, trailer towing, and the logistics of getting equipment to a live event safely and on time.", tone: "orange", color: "#F7931E" },
  { n: "05", title: "Event Setup", body: "Live setups — staking, anchoring, blower placement, cabling and the safety walkthrough that protects the customer and the operator.", tone: "blue", color: "#168CFF" },
  { n: "06", title: "Event Breakdown", body: "Safe breakdown, on-site cleaning, folding, loading and the warehouse return — the other half of the operation.", tone: "green", color: "#78C800" },
  { n: "07", title: "Safety & Weather", body: "Wind thresholds, weather cancellations, incident response, waivers and customer communication.", tone: "orange", color: "#F7931E" },
  { n: "08", title: "Team & Crew Management", body: "Roles, checklists, ride-alongs, communication and how a real crew is built and led.", tone: "blue", color: "#168CFF" },
];

export default function HandsOnTraining() {
  return (
    <div data-testid="handson-page" className="bg-black text-white">
      {/* Hero */}
      <section className="sect-black pt-36 md:pt-44 pb-16">
        <div className="bcu-container">
          <p className="bcu-eyebrow bcu-eyebrow-gold">Hands-On Training</p>
          <h1 className="mt-6 font-display text-white text-5xl md:text-7xl lg:text-[86px] leading-[1.02] font-medium max-w-4xl">
            Learn the business
            <span className="font-editorial italic font-medium text-[#F7931E]"> by doing the work.</span>
          </h1>
          <p className="mt-8 text-[#A9A9A9] text-[18px] leading-[1.75] max-w-[620px]">The hands-on component is designed to help students understand the physical, operational and customer-service demands of running an inflatable rental business — not the theory of one.</p>
        </div>
      </section>

      {/* Modules — alternating black tones, no image placeholders */}
      <section className="sect-black-3 py-20 md:py-24">
        <div className="bcu-container">
          <div className="grid md:grid-cols-2 gap-x-14 gap-y-16 pt-10 border-t border-[rgba(255,255,255,0.08)]">
            {SECTIONS.map((s) => (
              <div key={s.n} className="border-l pl-6" style={{ borderColor: s.color + "55" }}>
                <div className="text-[13px] tracking-[0.28em] uppercase font-semibold" style={{ color: s.color }}>Module {s.n}</div>
                <h2 className="mt-3 font-display text-3xl md:text-4xl text-white leading-tight font-medium">{s.title}</h2>
                <p className="mt-4 text-[#A9A9A9] text-[17px] leading-[1.75] max-w-md">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety + logistics */}
      <section className="sect-black py-20 md:py-24 border-t border-[rgba(255,255,255,0.08)]">
        <div className="bcu-container grid lg:grid-cols-2 gap-8">
          <div className="border border-[rgba(255,255,255,0.10)] p-8 md:p-10 rounded-md">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-4 w-4 text-[#D9A441]" />
              <div className="text-[11px] tracking-[0.28em] uppercase text-[#D9A441] font-semibold">Safety Notice</div>
            </div>
            <p className="mt-5 text-[#A9A9A9] text-[16px] leading-[1.75]">Participation may require students to follow safety instructions, wear appropriate clothing or protective equipment, sign waivers and meet physical participation requirements.</p>
          </div>
          <div className="border border-[rgba(217,164,65,0.24)] p-8 md:p-10 rounded-md">
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#D9A441] font-semibold">Logistics</div>
            <p className="mt-5 text-[#A9A9A9] text-[16px] leading-[1.75]">Hands-on training is held at the Bounce City Warehouse. Exact address, cohort dates, travel guidance, recommended lodging and what to bring are provided to accepted students during enrollment.</p>
            <p className="mt-4 text-sm text-[#7A7A7A]">Travel and accommodation are the student's responsibility unless otherwise specified in the signed agreement.</p>
          </div>
        </div>
        <div className="bcu-container mt-14 flex flex-wrap gap-4">
          <Link to="/apply" data-testid="handson-apply-btn" className="bcu-btn-primary">Apply now <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/blueprint" className="bcu-btn-secondary">See the 90-day blueprint</Link>
        </div>
      </section>
    </div>
  );
}
